import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { siteConfig } from '../../data/siteData';
import { Button } from '../ui/button';

const navItems = [
  {
    label: 'About',
    href: '/about',
    children: [
      { label: 'About Me', href: '/about' },
      { label: 'In The News', href: '/news' },
      { label: 'Videos', href: '/videos' },
      { label: 'Books', href: '/books' },
      { label: 'Awards', href: '/awards' },
    ],
  },
  { label: 'Topics', href: '/topics' },
  { label: 'Blog', href: '/blog' },
  {
    label: "Let's Engage",
    href: '/speaking',
    children: [
      { label: 'Speaking', href: '/speaking' },
      { label: 'Consulting', href: '/consulting' },
      { label: 'Advisory', href: '/advisory' },
    ],
  },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={siteConfig.logo}
              alt={siteConfig.name}
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`font-body font-medium text-sm tracking-wide flex items-center gap-1 py-2 transition-colors ${
                    location.pathname === item.href ||
                    item.children?.some((child) => location.pathname === child.href)
                      ? 'text-brand-secondary'
                      : 'text-brand-primary hover:text-brand-secondary'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                  {item.children && (
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        activeDropdown === item.label ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </Link>

                {/* Dropdown */}
                <AnimatePresence>
                  {item.children && activeDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 pt-2"
                    >
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[180px]">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                              location.pathname === child.href
                                ? 'text-brand-secondary bg-brand-accent'
                                : 'text-brand-primary hover:text-brand-secondary hover:bg-brand-accent'
                            }`}
                            data-testid={`nav-dropdown-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            <Button
              asChild
              className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold px-6"
              data-testid="nav-cta-btn"
            >
              <Link to="/contact">Get In Touch</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-brand-primary"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="max-w-7xl mx-auto px-6 py-4">
              {navItems.map((item) => (
                <div key={item.label} className="py-2">
                  {item.children ? (
                    <div>
                      <button
                        className="w-full flex items-center justify-between py-2 text-brand-primary font-medium"
                        onClick={() =>
                          setActiveDropdown(activeDropdown === item.label ? null : item.label)
                        }
                      >
                        {item.label}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            activeDropdown === item.label ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 overflow-hidden"
                          >
                            {item.children.map((child) => (
                              <Link
                                key={child.label}
                                to={child.href}
                                className="block py-2 text-slate-600 hover:text-brand-secondary"
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-2 text-brand-primary font-medium hover:text-brand-secondary"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <Button
                asChild
                className="w-full mt-4 rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold"
              >
                <Link to="/contact">Get In Touch</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
